import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { Box, Button, Chip, CircularProgress, IconButton, Typography } from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import LaunchIcon from '@material-ui/icons/Launch';
import { QRCodeSVG } from 'qrcode.react';
import _ from 'lodash';
import { withFirebase } from '../_firebase';
import { formatFirestoreTimestamp } from '../_helpers/mappers';
import { mapExpoAccountNameToUrlSlug } from '../_helpers/expoAccountMapper';
import JsonModal from '../StoresPage/JsonModal';
import FinishAppPublication from './FinishAppPublication';

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        overflowWrap: 'anywhere',
        padding: theme.spacing(2),
    },
    propertyContainer: {
        '& a': { color: 'black', textDecoration: 'none' },
    },
    label: { fontWeight: 'bold', marginBottom: 0 },
    value: {
        display: '-webkit-box',
        margin: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '-webkit-line-clamp': 2,
        '-webkit-box-orient': 'vertical',
    },
    iconButton: { marginLeft: 5 },
    icon: { fontSize: 16 },
    link: {
        color: '#1565c0 !important',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
    },
    buttonsContainer: {
        '& > *': { marginRight: theme.spacing(2) },
    },
    logsSection: { marginTop: theme.spacing(2) },
    logRow: {
        borderLeft: '3px solid #ddd',
        fontFamily: 'monospace',
        fontSize: 12,
        marginBottom: 4,
        padding: '2px 8px',
    },
    logError: { borderLeftColor: '#ff3d00', color: '#c0392b' },
    logInfo: { borderLeftColor: '#54A0FF' },
    logWarn: { borderLeftColor: '#F1C40F' },
}));

const stageLabel = {
    prepare: 'Preparação', firebase: 'Firebase', env: 'Ambiente', bundle: 'Bundle',
    version: 'Versão', webhook: 'Webhook', pipeline: 'Pipeline', build: 'Build', submit: 'Submit',
};

const FINISHABLE_STATUSES = ['built', 'submitted'];

function AppPublicationDetails({ publication, firebase, onDataUpdate }) {
    const classes = useStyles();

    const [logs, setLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(true);
    const [showJsonModal, setShowJsonModal] = useState(false);
    const [showFinishModal, setShowFinishModal] = useState(false);

    const loadLogs = () => {
        setLoadingLogs(true);
        firebase.getAppPublicationLogs(publication.id)
            .then(data => { setLogs(data); setLoadingLogs(false); })
            .catch(() => setLoadingLogs(false));
    };

    useEffect(() => {
        loadLogs();
    }, [publication.id]);

    const handleDataUpdate = () => {
        loadLogs();
        if (onDataUpdate) onDataUpdate();
    };

    const metadata = _.get(publication, 'metadata', {});
    const buildId = _.get(publication, 'buildId');
    const expoSlug = mapExpoAccountNameToUrlSlug(publication.expoAccountName);
    const buildUrl = buildId
        ? `https://expo.dev/accounts/${expoSlug}/projects/${metadata.appScheme}/builds/${buildId}`
        : '';

    const copyToClipboard = (value) => navigator.clipboard.writeText(value);

    function TextProperty({ data, label, name, value }) {
        const displayValue = value !== undefined ? value : data?.[name];
        return (
            <div className={classes.propertyContainer}>
                <p className={classes.label}>{label}
                    <IconButton className={classes.iconButton} onClick={() => copyToClipboard(displayValue)}>
                        <FileCopyIcon className={classes.icon} />
                    </IconButton>
                </p>
                <p className={classes.value}>{displayValue || '-'}</p>
            </div>
        );
    }

    function DateProperty({ data, label, name }) {
        return (
            <div className={classes.propertyContainer}>
                <p className={classes.label}>{label}</p>
                <p className={classes.value}>{formatFirestoreTimestamp(data?.[name], 'DD/MM/YYYY HH:mm') || '-'}</p>
            </div>
        );
    }

    function BooleanProperty({ value, label }) {
        return (
            <div className={classes.propertyContainer}>
                <p className={classes.label}>{label}</p>
                <p className={classes.value}>{value === true ? 'Sim' : value === false ? 'Não' : '-'}</p>
            </div>
        );
    }

    const finishDisabled = !FINISHABLE_STATUSES.includes(publication.status);

    return (
        <div className={classes.root}>
            <Grid container spacing={4}>
                <Grid item xs={3}>
                    <h2>Publicação</h2>
                    <TextProperty data={publication} label="ID da Publicação" name="id" />
                    <TextProperty data={publication} label="ID da Loja" name="storeId" />
                    <TextProperty data={publication} label="Plataforma e-commerce" name="platformId" />
                    <TextProperty data={publication} label="Perfil de build" name="buildProfile" />
                    <BooleanProperty label="Aplicativo novo" value={publication.isNewApp} />
                    <BooleanProperty label="Auto-submit" value={publication.autoSubmit} />
                </Grid>

                <Grid item xs={3}>
                    <h2>Expo & Firebase</h2>
                    <TextProperty data={publication} label="ID do Projeto Expo" name="expoProjectId" />
                    <TextProperty data={publication} label="Nome da conta Expo" name="expoAccountName" />
                    <TextProperty data={metadata} label="App scheme" name="appScheme" />
                    <TextProperty data={publication} label="Projeto do Firebase" name="firebaseProjectName" />
                    <TextProperty data={publication} label="ID do Aplicativo Firebase" name="firebaseApplicationId" />
                    <div className={classes.propertyContainer}>
                        <p className={classes.label}>Build ID</p>
                        <p className={classes.value}>
                            {buildId
                                ? <a className={classes.link} href={buildUrl} target="_blank" rel="noreferrer">
                                    {buildId.slice(0, 8)}… <LaunchIcon style={{ fontSize: 14 }} />
                                  </a>
                                : '-'}
                        </p>
                    </div>
                    <div className={classes.propertyContainer}>
                        <p className={classes.label}>Submit ID</p>
                        <p className={classes.value}>
                            {publication.submitId
                                ? <a className={classes.link} href={`https://expo.dev/accounts/${expoSlug}/projects/${metadata.appScheme}/submissions/${publication.submitId}`} target="_blank" rel="noreferrer">
                                    {publication.submitId.slice(0, 8)}… <LaunchIcon style={{ fontSize: 14 }} />
                                  </a>
                                : '-'}
                        </p>
                    </div>
                </Grid>

                <Grid item xs={3}>
                    <h2>App & Versão</h2>
                    <TextProperty data={metadata} label="Nome do aplicativo" name="appName" />
                    <TextProperty data={metadata} label="Bundle ID" name="bundleId" />
                    <TextProperty data={metadata} label="Marketing version" name="marketingVersion" />
                    <TextProperty data={metadata} label="Build version" name="buildVersion" />
                    {publication.mobilePlatform === 'android' && (
                        <TextProperty data={publication} label="Nome da conta de serviço" name="googlePlayServiceAccountName" />
                    )}
                </Grid>

                <Grid item xs={3}>
                    <h2>Artefatos</h2>
                    <div className={classes.propertyContainer}>
                        <p className={classes.label}>Build gerado</p>
                        <p className={classes.value}>
                            {publication.artifactUrl
                                ? <a className={classes.link} href={publication.artifactUrl} target="_blank" rel="noreferrer">
                                    Baixar (.aab / .apk / .ipa) <LaunchIcon style={{ fontSize: 14 }} />
                                  </a>
                                : '-'}
                        </p>
                    </div>
                    <DateProperty data={publication} label="Criado em" name="createdAt" />
                    <DateProperty data={publication} label="Última atualização" name="updatedAt" />
                    <DateProperty data={publication} label="Concluído em" name="finishedAt" />
                    {publication.buildProfile === 'store-staging' && (publication.artifactUrl || buildUrl) && (() => {
                        const qrUrl = publication.mobilePlatform === 'android' ? publication.artifactUrl : buildUrl;
                        return (
                            <Box mt={2}>
                                <p className={classes.label}>QR Code para testes</p>
                                <Box mt={1}>
                                    <QRCodeSVG value={qrUrl} size={100} />
                                    <Box mt={1}>
                                        <a href={qrUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#1565c0' }}>
                                            Abrir no Expo
                                        </a>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })()}
                    {publication.error && (
                        <Box mt={1} p={1} style={{ backgroundColor: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: 4 }}>
                            <Typography variant="body2" color="error">
                                <strong>Erro ({publication.error.stage}):</strong> {publication.error.message}
                            </Typography>
                        </Box>
                    )}
                </Grid>

                <Grid item xs={12}>
                    <div className={classes.logsSection}>
                        <Typography variant="subtitle2" gutterBottom>Timeline de logs</Typography>
                        {loadingLogs
                            ? <CircularProgress size={20} />
                            : logs.length === 0
                                ? <Typography variant="caption" color="textSecondary">Sem logs registrados.</Typography>
                                : logs.map(log => (
                                    <div
                                        key={log.id}
                                        className={`${classes.logRow} ${log.level === 'error' ? classes.logError : log.level === 'warn' ? classes.logWarn : classes.logInfo}`}
                                    >
                                        <span style={{ color: '#888', marginRight: 6 }}>
                                            {formatFirestoreTimestamp(log.createdAt, 'HH:mm:ss')}
                                        </span>
                                        <Chip label={stageLabel[log.stage] || log.stage} size="small" style={{ fontSize: 10, height: 16, marginRight: 6 }} />
                                        {log.message}
                                    </div>
                                ))
                        }
                    </div>
                </Grid>

                <Grid alignItems="center" className={classes.buttonsContainer} container direction="row" item justify="flex-start" xs={12}>
                    <Button
                        disabled={finishDisabled}
                        variant="contained"
                        onClick={() => setShowFinishModal(true)}
                    >
                        Concluir publicação
                    </Button>
                    <Button variant="contained" onClick={() => setShowJsonModal(true)}>Ver JSON</Button>
                </Grid>
            </Grid>

            <JsonModal
                data={publication}
                isOpen={showJsonModal}
                onClose={() => setShowJsonModal(false)}
            />

            <FinishAppPublication
                publication={publication}
                isOpen={showFinishModal}
                onClose={() => setShowFinishModal(false)}
                onDataUpdate={handleDataUpdate}
            />
        </div>
    );
}

export default withFirebase(AppPublicationDetails);
